<template>
    <div class="col-xs-12 col-md-6 col-xl-4">
        <div class="card my-2">
            <div class="card-header">
                <label class="form-label" for="no-of-rooms" id="no-of-rooms">{{ room.element }}</label>
            </div>
            <div class="card-body">
                <input id="no-of-rooms" type="number" class="form-control mr-auto" placeholder="No. of rooms" 
                            min="0" :max="maxRooms" :value="numberOfRooms" 
                            :class="{ invalid: $v.numberOfRooms.$error }"
                            @blur="$v.numberOfRooms.$touch()"
                            @input="onNumberOfRoomsChanged($event.target.value)" 
                            :disabled="disabled">
                <div v-if="$v.numberOfRooms.$error && !$v.numberOfRooms.between" class="validation-message">
                    Please select between {{ $v.numberOfRooms.$params.between.min }} and {{ $v.numberOfRooms.$params.between.max }} rooms.
                </div>
            </div>
            <div class="card-footer">
                <div class="input-group">
                    <div class="input-group-prepend">
                        <span class="input-group-text" id="grade">Rating</span>
                    </div>
                    <select id="grade" class="form-control" v-model.number="gradeId" :disabled="numberOfRooms == 0">
                        <option class="form-control-item" 
                            v-for="grade in room.grades" :key="grade.gradeId" :value="grade.gradeId">{{ grade.grade }}</option>
                    </select>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { between } from 'vuelidate/lib/validators'

export default {
    props: { 
        productId: { 
            type: Number, 
            required: true
        },
        room: { 
            type: Object, 
            required: true
        },
        passengersToRoom: {
            type: Number,
            required: true
        }
    },
    data() { 
        return { 
            gradeId: this.room.grades[0].gradeId,
            numberOfRooms: 0,
            passengersInRoom: []
        }
    },
    validations() {
        return {
            numberOfRooms: {
                between(value) {
                    return between(0, this.maxRooms)(value);
                },
            },
        }
    },
    computed: {
        passengerCount() {
            if (this.passengersInRoom.length == 0) { 
                return 0;
            }
            this.passengersInRoom.reduce((sum, cur) => { return sum + cur });
        },
        actualRooms() { return this.passengersInRoom.length; },
        maxRooms() {
            // if we have over allocated
            if (this.passengersToRoom == 0) {
                return this.actualRooms; 
            }

            // otherwise get the number of rooms
            let rooms = this.actualRooms;
            
            // and then see how many additional rooms we can fill
            if (this.passengersToRoom >= this.room.min) {
                let remainingPassengers = this.passengersToRoom;

                // add one room for all rooms we can fill to the maximum
                while(remainingPassengers >= this.room.max) {
                    rooms += 1;
                    remainingPassengers -= this.room.max;
                }

                // add one room if the remaining passengers
                // meet the minimum for this room
                if (remainingPassengers >= this.room.min) {
                    rooms += 1;
                }
            }

            return rooms;
        },
        disabled() { 
            return this.room.min > this.passengersToRoom && this.passengerCount == 0;
        }
    },
    watch: { 
        gradeId: function(val) {
            this.$emit('gradeSet', this.room.elementId, val);
        }
    },
    methods: {
        ...mapGetters([ 'getRoomById' ]),
        onNumberOfRoomsChanged(value) {
            value = Number.parseFloat(value);

            if (value == NaN || !Number.isInteger(value)) {
                // invalid number remove all existing rooms
                while(this.passengerCount > 0) {
                    let passengers = this.passengersInRoom.pop();
                    this.$emit('removeRoom', this.room.elementId, passengers);
                }

                this.numberOfRooms = 0;
            } else {
                // valid integer
                if (value < this.actualRooms) {
                    // get the number of rooms to remove
                    let diff = this.actualRooms - value;

                    // don't remove more rooms than we have (but let an invalid value be set)
                    while(diff > 0 && this.passengersInRoom.length > 0) {
                        let passengers = this.passengersInRoom.pop();
                        this.$emit('removeRoom', this.room.elementId, passengers);
                        diff--;
                    }
                } else if (value > this.actualRooms) {
                    // get the number of rooms to add
                    let diff = value - this.actualRooms;

                    // because passengers to rooms is bound it will not be updated in the loop below
                    // this causes issues with maxRooms calculation so copy current max value
                    let max = this.maxRooms;

                    // don't add more rooms than we can (but let an invalid value be set)
                    while(diff > 0 && max > this.actualRooms) {
                        // push if incremented
                        let passengers = Math.min(this.room.max, this.passengersToRoom);
                        this.passengersInRoom.push(passengers);
                        this.$emit('addRoom', this.room.elementId, this.gradeId, passengers);
                        diff--;
                    }
                }
                this.numberOfRooms = value;
            }

            this.$emit('onValidate');
        },
    },
    created: function() { 
        // load current rooming if it exists
        let roomState = this.$store.getters.getRoomById(this.productId, this.room.elementId);
        if (roomState) {
            this.gradeId = roomState.gradeId;
            this.passengersInRoom = roomState.passengersInRoom;
            this.numberOfRooms = this.passengersInRoom.length;
        }
    }
}
</script>